/**
 * Canonical license-status compliance semantics (PD-14745) — the TypeScript
 * mirror of the mission-control `lm_status_canonical` table, for the frontend +
 * backend consumers that can't join the DB (checkout good-standing gate, list
 * filters, status chips/colors).
 *
 * Source of truth chain: the scripts/license-management classifier
 * (`CANONICAL_STATUS_META` in `lib/license-status.mjs`) seeds the DB table;
 * this constant is the hand-kept TS copy, guarded by
 * `license-status-canonical.test.ts` so the two representations can't drift.
 *
 * Two ORTHOGONAL, hard-boolean flags (a flag IS the impact, and the impact is
 * binary — no null/indeterminate):
 *   canOrder  — is the license valid / in-force, so the holder may place an
 *               order? TRUE for Active, Probation and Limited. This is BOTH the
 *               order gate AND the client-level "covered" test.
 *   isAdverse — did the board take a disciplinary action worth review / watch?
 *               TRUE for Suspended/Revoked/Surrendered/Denied AND Probation and
 *               Limited (the orthogonal case: can order, but on watch).
 *
 * Derived, NOT stored: "good standing" = canOrder && !isAdverse. Pending/Unknown
 * are (false, false); their "needs review" meaning is recoverable from the
 * status name, not overloaded into a third flag state.
 */

export const CANONICAL_LICENSE_STATUSES = [
	'Active',
	'Expired',
	'Inactive',
	'Pending',
	'Probation',
	'Limited',
	'Suspended',
	'Revoked',
	'Surrendered',
	'Denied',
	'Cancelled',
	// PD-15391 — Retired is TERMINAL and Inactive is not. Kept as its own status
	// rather than folded into Inactive because a retired licence never returns
	// while an inactive one routinely does, and the label reaches users. The
	// registry-side twin lives in
	// scripts/license-management/lib/license-status.mjs; the two vocabularies
	// must be changed together.
	'Retired',
	'Deceased',
	'Unknown',
] as const;

export type CanonicalLicenseStatus =
	(typeof CANONICAL_LICENSE_STATUSES)[number];

export interface CanonicalStatusFlagsModel {
	canOrder: boolean;
	isAdverse: boolean;
	/**
	 * Can a licence in this status ever come back? (PD-15391) Deliberately NOT
	 * the same question as `canOrder`: Expired and Inactive are both
	 * non-orderable and both routinely return, so they are `terminal: false`.
	 * Read this flag rather than keeping a local list of dead statuses.
	 */
	terminal: boolean;
}

export const CANONICAL_STATUS_META: Record<
	CanonicalLicenseStatus,
	CanonicalStatusFlagsModel
> = {
	Active: { canOrder: true, isAdverse: false, terminal: false },
	Probation: { canOrder: true, isAdverse: true, terminal: false },
	// A licence the board has RESTRICTED in scope but not stopped (KS/IA/AK
	// "Limited", WI "Licensed (Limited)"). Previously fell through to Unknown,
	// which was wrong in both directions at once: canOrder false blocked a licence
	// that is in force, and isAdverse false hid a restriction a reviewer should
	// see. Same flag profile as Probation, kept under its own name because the
	// board did not say "probation" and the label is shown to users.
	Limited: { canOrder: true, isAdverse: true, terminal: false },
	Suspended: { canOrder: false, isAdverse: true, terminal: false },
	Revoked: { canOrder: false, isAdverse: true, terminal: true },
	Surrendered: { canOrder: false, isAdverse: true, terminal: true },
	Denied: { canOrder: false, isAdverse: true, terminal: false },
	Expired: { canOrder: false, isAdverse: false, terminal: false },
	Inactive: { canOrder: false, isAdverse: false, terminal: false },
	Cancelled: { canOrder: false, isAdverse: false, terminal: true },
	// Retiring is not a board action against the licensee, so isAdverse stays
	// false — but it is permanent, which Inactive is not.
	Retired: { canOrder: false, isAdverse: false, terminal: true },
	Deceased: { canOrder: false, isAdverse: false, terminal: true },
	Pending: { canOrder: false, isAdverse: false, terminal: false },
	Unknown: { canOrder: false, isAdverse: false, terminal: false },
};

/** Canonical statuses whose holder may place an order (Active, Probation, Limited). */
export const CAN_ORDER_STATUSES: ReadonlyArray<CanonicalLicenseStatus> =
	CANONICAL_LICENSE_STATUSES.filter((s) => CANONICAL_STATUS_META[s].canOrder);

/** Canonical statuses under a board action worth review/watch. */
export const ADVERSE_STATUSES: ReadonlyArray<CanonicalLicenseStatus> =
	CANONICAL_LICENSE_STATUSES.filter((s) => CANONICAL_STATUS_META[s].isAdverse);

/** Canonical statuses a licence can never return from (PD-15391). */
export const TERMINAL_STATUSES: ReadonlyArray<CanonicalLicenseStatus> =
	CANONICAL_LICENSE_STATUSES.filter((s) => CANONICAL_STATUS_META[s].terminal);

// Case-insensitive lookup: registry statuses arrive in canonical case, but
// tenant-mirrored / legacy values can vary in case, so normalize before lookup.
const FLAGS_BY_LOWER: Record<string, CanonicalStatusFlagsModel> =
	Object.fromEntries(
		Object.entries(CANONICAL_STATUS_META).map(([k, v]) => [k.toLowerCase(), v]),
	);

/**
 * Is a license under this status valid/in-force so the holder may order?
 * Unknown/unmapped/null statuses return `false` (conservative — never assume
 * order eligibility from a status we don't recognize).
 */
export function statusCanOrder(status: string | null | undefined): boolean {
	if (status == null) {
		return false;
	}
	return FLAGS_BY_LOWER[status.toLowerCase()]?.canOrder === true;
}

/**
 * Is a license under this status flagged with a board action worth review/watch?
 * Unknown/unmapped/null statuses return `false`.
 */
export function statusIsAdverse(status: string | null | undefined): boolean {
	if (status == null) {
		return false;
	}
	return FLAGS_BY_LOWER[status.toLowerCase()]?.isAdverse === true;
}

/** Is this licence permanently dead — retired, deceased, revoked, surrendered, cancelled? */
export function statusIsTerminal(status: string | null | undefined): boolean {
	if (status == null) {
		return false;
	}
	return FLAGS_BY_LOWER[status.toLowerCase()]?.terminal === true;
}
